import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Sync status states
export const SYNC_STATUS = {
  IDLE: 'idle',           // No recent sync activity
  SYNCING: 'syncing',     // Currently syncing
  SYNCED: 'synced',       // Successfully synced
  OFFLINE: 'offline',     // No network connection
  ERROR: 'error',         // Sync failed
  BACKING_UP: 'backing_up', // Creating backup
  BACKED_UP: 'backed_up'    // Backup complete
};

const SyncContext = createContext(null);

export function SyncProvider({ children }) {
  const [status, setStatus] = useState(SYNC_STATUS.IDLE);
  const [lastSynced, setLastSynced] = useState(null);
  const [lastBackup, setLastBackup] = useState(() => {
    // Load last backup date from localStorage
    try {
      const saved = localStorage.getItem('gym_notebook_last_backup');
      return saved ? new Date(saved) : null;
    } catch {
      return null;
    }
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Track active sync operations
  const activeOps = useRef(0);
  const syncTimeout = useRef(null);

  // Start a sync operation
  const startSync = useCallback(() => {
    activeOps.current += 1;
    setPendingCount(activeOps.current);
    setStatus(SYNC_STATUS.SYNCING);
    setErrorMessage(null);
    
    // Clear any existing timeout
    if (syncTimeout.current) {
      clearTimeout(syncTimeout.current);
    }
  }, []);

  // Complete a sync operation successfully
  const completeSync = useCallback(() => {
    activeOps.current = Math.max(0, activeOps.current - 1);
    setPendingCount(activeOps.current);
    
    if (activeOps.current === 0) {
      setStatus(SYNC_STATUS.SYNCED);
      setLastSynced(new Date());
      
      // Return to idle after 3 seconds
      syncTimeout.current = setTimeout(() => {
        setStatus(SYNC_STATUS.IDLE);
      }, 3000);
    }
  }, []);

  // Mark sync as failed
  const failSync = useCallback((error) => {
    activeOps.current = Math.max(0, activeOps.current - 1);
    setPendingCount(activeOps.current);
    
    if (activeOps.current === 0) {
      // Check if it's a network error
      if (!navigator.onLine) {
        setStatus(SYNC_STATUS.OFFLINE);
        setErrorMessage('No internet connection');
      } else {
        setStatus(SYNC_STATUS.ERROR);
        setErrorMessage(error?.message || 'Sync failed');
      }
      
      // Return to idle after 5 seconds
      syncTimeout.current = setTimeout(() => {
        setStatus(SYNC_STATUS.IDLE);
        setErrorMessage(null);
      }, 5000);
    }
  }, []);

  // Create a cloud backup (automatic)
  const createBackup = useCallback(async (api, silent = true) => {
    // Check if already backed up today
    if (lastBackup) {
      const today = new Date().toDateString();
      const lastBackupDay = new Date(lastBackup).toDateString();
      if (today === lastBackupDay) {
        // Already backed up today
        return { skipped: true, reason: 'Already backed up today' };
      }
    }
    
    // Check if online
    if (!navigator.onLine) {
      if (!silent) {
        setStatus(SYNC_STATUS.OFFLINE);
      }
      return { error: 'No internet connection' };
    }
    
    try {
      setStatus(SYNC_STATUS.BACKING_UP);
      
      // Call the backup API
      const result = await api.post('/backup/create', {});
      
      // Update last backup time
      const now = new Date();
      setLastBackup(now);
      localStorage.setItem('gym_notebook_last_backup', now.toISOString());
      
      setStatus(SYNC_STATUS.BACKED_UP);
      
      // Return to idle after 3 seconds
      syncTimeout.current = setTimeout(() => {
        setStatus(SYNC_STATUS.IDLE);
      }, 3000);
      
      return { success: true, backup: result };
    } catch (error) {
      console.error('Backup failed:', error);
      if (!silent) {
        setStatus(SYNC_STATUS.ERROR);
        setErrorMessage('Backup failed');
        syncTimeout.current = setTimeout(() => {
          setStatus(SYNC_STATUS.IDLE);
          setErrorMessage(null);
        }, 5000);
      } else {
        setStatus(SYNC_STATUS.IDLE);
      }
      return { error: error.message };
    }
  }, [lastBackup]);

  // Wrapper for fetch that tracks sync status
  const syncFetch = useCallback(async (url, options = {}) => {
    startSync();
    try {
      const response = await fetch(url, options);
      if (response.ok) {
        completeSync();
      } else {
        failSync(new Error(`HTTP ${response.status}`));
      }
      return response;
    } catch (error) {
      failSync(error);
      throw error;
    }
  }, [startSync, completeSync, failSync]);

  // Check online status
  React.useEffect(() => {
    const handleOnline = () => {
      if (status === SYNC_STATUS.OFFLINE) {
        setStatus(SYNC_STATUS.IDLE);
        setErrorMessage(null);
      }
    };
    
    const handleOffline = () => {
      setStatus(SYNC_STATUS.OFFLINE);
      setErrorMessage('No internet connection');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Initial check
    if (!navigator.onLine) {
      setStatus(SYNC_STATUS.OFFLINE);
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (syncTimeout.current) {
        clearTimeout(syncTimeout.current);
      }
    };
  }, [status]);

  const value = {
    status,
    lastSynced,
    lastBackup,
    pendingCount,
    errorMessage,
    startSync,
    completeSync,
    failSync,
    syncFetch,
    createBackup,
    isOnline: navigator.onLine
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}

export default SyncContext;
